import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Copy, Share2, BarChart3, Crown } from "lucide-react";
import { useTranslation } from "react-i18next";
import { ResumeAnalyticsDialog } from "./ResumeAnalyticsDialog";
import {AutoTranslate} from "@/lib/autoTranslate.tsx";

interface ShareDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  resumeId: string;
  resumeTitle: string;
}

export function ShareDialog({ open, onOpenChange, resumeId, resumeTitle }: ShareDialogProps) {
  const { t } = useTranslation();
  const [shareLink, setShareLink] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [sharedResumeId, setSharedResumeId] = useState<string>("");
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [isPro, setIsPro] = useState(false);

  useEffect(() => {
    if (!open) {
      return;
    }

    checkSubscription();
    loadExistingShareLink();
  }, [open, resumeId]);

  const buildShareLink = (token: string) =>
    `${window.location.origin}/shared/${token}`;

  const checkSubscription = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      const { data } = await supabase
        .from("profiles")
        .select("subscription_tier")
        .eq("user_id", user.id)
        .single();
      setIsPro(data?.subscription_tier === "pro");
    }
  };

  const loadExistingShareLink = async () => {
    setShareLink("");
    setSharedResumeId("");

    if (!resumeId) {
      return;
    }

    setLoading(true);
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();

      if (!user) {
        return;
      }

      const { data: existing, error } = await supabase
        .from("shared_resumes")
        .select("id, share_token")
        .eq("resume_id", resumeId)
        .eq("user_id", user.id)
        .maybeSingle();

      if (error) throw error;

      if (existing?.share_token) {
        setShareLink(buildShareLink(existing.share_token));
        setSharedResumeId(existing.id);
      }
    } catch (error) {
      console.error("Failed to load existing share link", error);
    } finally {
      setLoading(false);
    }
  };

  const generateShareLink = async () => {
    setLoading(true);
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();

      if (!user) {
        throw new Error("You must be signed in to share a resume.");
      }

      // Check if already shared
      const { data: existing, error: existingError } = await supabase
        .from("shared_resumes")
        .select("id, share_token")
        .eq("resume_id", resumeId)
        .eq("user_id", user.id)
        .maybeSingle();

      if (existingError) throw existingError;

      let token: string;
      let sharedId: string;
      if (existing) {
        token = existing.share_token;
        sharedId = existing.id;
      } else {
        // Generate new share token
        token = crypto.randomUUID();
        const { data: newShare, error } = await supabase.from("shared_resumes").insert({
          resume_id: resumeId,
          user_id: user.id,
          share_token: token,
        }).select().single();

        if (error) throw error;
        sharedId = newShare.id;
      }

      const link = buildShareLink(token);
      setShareLink(link);
      setSharedResumeId(sharedId);
      toast.success(existing ? "Share link loaded!" : "Share link generated!");
    } catch (error: any) {
      toast.error("Failed to generate share link");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(shareLink);
    toast.success("Link copied to clipboard!");
  };

  const shareToSocial = (platform: string) => {
    const text = `Check out my resume: ${resumeTitle}`;
    const encodedLink = encodeURIComponent(shareLink);
    const encodedText = encodeURIComponent(text);

    const urls: Record<string, string> = {
      whatsapp: `https://wa.me/?text=${encodedText}%20${encodedLink}`,
      twitter: `https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedLink}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedLink}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedLink}`,
      instagram: `https://www.instagram.com/`,
      tiktok: `https://www.tiktok.com/`,
    };

    if (urls[platform]) {
      window.open(urls[platform], "_blank", "width=600,height=400");
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t("share.title")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {!shareLink ? (
              <Button onClick={generateShareLink} disabled={loading} className="w-full">
                <Share2 className="mr-2 h-4 w-4" />
                {t("share.generateLink")}
              </Button>
            ) : (
              <>
                  <div className="flex items-center gap-2">
                  <Input value={shareLink} readOnly />
                  <Button onClick={copyToClipboard} size="icon" variant="outline" aria-label="Copy share link">
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>

                <div className="space-y-2">
                  <p className="text-sm text-muted-foreground">{t("share.shareOn")}:</p>
                  <div className="grid grid-cols-3 gap-2">
                    <Button variant="outline" onClick={() => shareToSocial("whatsapp")}>
                      WhatsApp
                    </Button>
                    <Button variant="outline" onClick={() => shareToSocial("twitter")}>
                      Twitter
                    </Button>
                    <Button variant="outline" onClick={() => shareToSocial("facebook")}>
                      Facebook
                    </Button>
                    <Button variant="outline" onClick={() => shareToSocial("linkedin")}>
                      LinkedIn
                    </Button>
                    <Button variant="outline" onClick={() => shareToSocial("instagram")}>
                      Instagram
                    </Button>
                    <Button variant="outline" onClick={() => shareToSocial("tiktok")}>
                      TikTok
                    </Button>
                  </div>
                </div>

                {/* Resume Analytics Button */}
                <div className="pt-2 border-t border-border">
                  {isPro ? (
                    <Button
                      onClick={() => setShowAnalytics(true)}
                      variant="outline"
                      className="w-full"
                    >
                      <BarChart3 className="mr-2 h-4 w-4" />
                      {AutoTranslate(`View Resume Analytics`)}
                    </Button>
                  ) : (
                    <Button
                      variant="outline"
                      className="w-full opacity-70"
                      disabled
                    >
                      <Crown className="mr-2 h-4 w-4 text-yellow-500" />
                      {AutoTranslate(`Resume Analytics (Pro Only)`)}
                    </Button>
                  )}
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Analytics Dialog */}
      <ResumeAnalyticsDialog
        open={showAnalytics}
        onOpenChange={setShowAnalytics}
        sharedResumeId={sharedResumeId}
        resumeTitle={resumeTitle}
      />
    </>
  );
}
